import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo, { breadcrumbs } from "../components/Seo";
import { createLead } from "../leads/api";
import "./Questionnaire.css";

const START_PRICE = "$8,850";

const PROJECT_TYPES = [
  { id: "web-app", label: "Web app", hint: "Dashboard, SaaS, internal tool" },
  { id: "ecommerce", label: "E-commerce", hint: "Store, catalog, checkout" },
  { id: "mobile-app", label: "Mobile app", hint: "iOS, Android, or both" },
  { id: "marketing", label: "Marketing site", hint: "Brand, landing, content" },
  { id: "full-stack", label: "Full product", hint: "Web + mobile + backend" },
  { id: "just-looking", label: "Just browsing", hint: "No project yet" },
];

const PLATFORMS = ["Web", "iOS", "Android", "Responsive mobile web"];

const FEATURES = [
  "Accounts & auth",
  "Payments / checkout",
  "Subscriptions",
  "Live streaming",
  "Push notifications",
  "Chat / messaging",
  "Admin dashboard",
  "CMS / editor",
  "Marketplace",
  "Search",
  "Integrations (CRM, email, etc)",
  "AI / machine learning",
];

const BUDGET_TIERS = [
  {
    id: "starter",
    label: `${START_PRICE}+`,
    hint: "Starter — focused single-platform build",
  },
  { id: "mid", label: "$12.8k+", hint: "Mid — full product, single platform" },
  { id: "full", label: "$24.8k+", hint: "Full stack — web + mobile + backend" },
  { id: "custom", label: "Not sure yet", hint: "Recommend based on scope" },
];

const TIMELINES = [
  "< 1 month — urgent",
  "1–3 months",
  "3–6 months",
  "6+ months",
  "Flexible / exploring",
];

const FULL_STEPS = [
  "Project type",
  "Platforms",
  "Features",
  "Timeline",
  "Budget",
  "About you",
  "Review",
];
const BROWSING_STEPS = ["Project type", "Just browsing"];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const QMeta = ({ children, red, style }) => (
  <div className={`fwq-meta${red ? " fwq-meta--red" : ""}`} style={style}>
    {children}
  </div>
);

const ChoiceCard = ({ selected, onClick, children, style }) => (
  <button
    type="button"
    className={`fwq-card${selected ? " fwq-card--on" : ""}`}
    onClick={onClick}
    style={style}
  >
    {children}
  </button>
);

const Chip = ({ selected, onClick, children }) => (
  <button
    type="button"
    className={`fwq-chip${selected ? " fwq-chip--on" : ""}`}
    onClick={onClick}
    aria-pressed={selected}
  >
    {children}
  </button>
);

const TextInput = ({
  value,
  onChange,
  placeholder,
  multi,
  id,
  type = "text",
}) => {
  const Tag = multi ? "textarea" : "input";
  return (
    <Tag
      id={id}
      type={multi ? undefined : type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={multi ? 4 : undefined}
      className="fwq-input"
    />
  );
};

const useFormState = () => {
  const [data, setData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fw-q") || "{}");
    } catch {
      return {};
    }
  });
  const set = (patch) =>
    setData((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem("fw-q", JSON.stringify(next));
      return next;
    });
  const reset = () => {
    localStorage.removeItem("fw-q");
    setData({});
  };
  return [data, set, reset];
};

const Questionnaire = () => {
  const [data, set, resetData] = useFormState();
  const [step, setStep] = useState(() =>
    parseInt(localStorage.getItem("fw-q-step") || "0", 10),
  );
  const [submitState, setSubmitState] = useState({
    status: "idle",
    error: null,
  });
  const [showValidation, setShowValidation] = useState(false);

  const isBrowsing = data.projectType === "just-looking";
  const visibleSteps = isBrowsing ? BROWSING_STEPS : FULL_STEPS;

  useEffect(() => {
    localStorage.setItem("fw-q-step", String(step));
  }, [step]);

  // Clamp step when branching changes step count.
  useEffect(() => {
    if (step > visibleSteps.length - 1) setStep(visibleSteps.length - 1);
  }, [visibleSteps.length, step]);

  const atEnd = step >= visibleSteps.length - 1;
  const go = (d) =>
    setStep((s) => Math.max(0, Math.min(visibleSteps.length - 1, s + d)));

  const canAdvance = useMemo(() => {
    if (step === 0) return !!data.projectType;
    if (!isBrowsing && step === 5) {
      return (
        (data.name || "").trim().length > 0 && emailRe.test(data.email || "")
      );
    }
    return true;
  }, [step, data, isBrowsing]);

  const handleReset = () => {
    if (!window.confirm("Reset the form? Your progress will be cleared."))
      return;
    resetData();
    localStorage.removeItem("fw-q-step");
    setStep(0);
    setShowValidation(false);
    setSubmitState({ status: "idle", error: null });
  };

  const handleSubmit = async () => {
    if (!isBrowsing) {
      if (!data.name || !emailRe.test(data.email || "")) {
        setShowValidation(true);
        setStep(5);
        return;
      }
    }
    setSubmitState({ status: "submitting", error: null });
    try {
      await createLead({
        ...data,
        intent: isBrowsing ? "book-intro" : "brief",
      });
      localStorage.removeItem("fw-q");
      localStorage.removeItem("fw-q-step");
      setSubmitState({ status: "success", error: null });
    } catch (err) {
      setSubmitState({
        status: "idle",
        error: err.message || "Something went wrong. Try again.",
      });
    }
  };

  if (submitState.status === "success") {
    return (
      <div className="fwq">
        {/* A confirmation screen has nothing to rank for, and indexing it
            would put a dead-end page in front of searchers. */}
        <Seo
          title="Thanks — bboy creative"
          description="Brief received. We'll be in touch within 24 hours."
          path="/start"
          noindex
        />
        <header className="fwq-header">
          <Link to="/" className="fwq-wordmark" style={{ textDecoration: "none" }}>
            bboy<span>/</span>creative
          </Link>
          <QMeta>BRIEF RECEIVED</QMeta>
          <span />
        </header>
        <main className="fwq-main fwq-success">
          <QMeta>BRIEF RECEIVED</QMeta>
          <h1 className="fwq-h1">
            Talk <span className="fwq-red">soon.</span>
          </h1>
          <p className="fwq-sub">
            {isBrowsing
              ? "We'll reach out with a proposed time for a 30-min intro."
              : "We read every submission. You'll hear back within 24 hours with next steps — usually a proposed discovery call time."}
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="fwq">
      <Seo
        title="Start a project — bboy creative"
        description="Tell us what you're building. A two-minute brief gets you a scoped, fixed-price proposal from bboy creative."
        path="/start"
        jsonLd={breadcrumbs([
          { name: "Home", path: "/" },
          { name: "Start a project", path: "/start" },
        ])}
      />

      <header className="fwq-header">
        <Link to="/" className="fwq-wordmark" style={{ textDecoration: "none" }}>
          bboy<span>/</span>creative
        </Link>
        <QMeta>START A PROJECT · INTAKE FORM</QMeta>
        <button type="button" className="fwq-reset" onClick={handleReset}>
          Reset
        </button>
      </header>

      <div className="fwq-progress">
        <div
          className="fwq-progress__bar"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={visibleSteps.length}
          aria-valuenow={step + 1}
        >
          {visibleSteps.map((_, i) => (
            <div
              key={i}
              className={`fwq-progress__seg${i <= step ? " on" : ""}`}
            />
          ))}
        </div>
        <div className="fwq-progress__meta">
          <QMeta>
            Step {step + 1} / {visibleSteps.length}
          </QMeta>
          <QMeta>{visibleSteps[step]}</QMeta>
        </div>
      </div>

      <main className="fwq-main" aria-live="polite">
        {step === 0 && <StepProjectType data={data} set={set} />}
        {!isBrowsing && step === 1 && <StepPlatforms data={data} set={set} />}
        {!isBrowsing && step === 2 && <StepFeatures data={data} set={set} />}
        {!isBrowsing && step === 3 && <StepTimeline data={data} set={set} />}
        {!isBrowsing && step === 4 && <StepBudget data={data} set={set} />}
        {!isBrowsing && step === 5 && (
          <StepContact data={data} set={set} showValidation={showValidation} />
        )}
        {!isBrowsing && step === 6 && <StepReview data={data} />}
        {isBrowsing && step === 1 && <BrowsingOutcome />}
      </main>

      <footer className="fwq-footer">
        <button
          type="button"
          className="fwq-back"
          onClick={() => go(-1)}
          disabled={step === 0}
        >
          ← Back
        </button>

        {submitState.error && (
          <span className="fwq-err">{submitState.error}</span>
        )}

        {atEnd ? (
          <button
            type="button"
            className="fwq-primary fwq-primary--red"
            onClick={handleSubmit}
            disabled={submitState.status === "submitting"}
          >
            {submitState.status === "submitting"
              ? "Sending…"
              : isBrowsing
              ? "Book a 30-min intro →"
              : "Send brief →"}
          </button>
        ) : (
          <button
            type="button"
            className="fwq-primary"
            onClick={() => go(1)}
            disabled={!canAdvance}
          >
            Next →
          </button>
        )}
      </footer>
    </div>
  );
};

const StepProjectType = ({ data, set }) => (
  <div>
    <QMeta>QUESTION 01</QMeta>
    <h1 className="fwq-h1">
      What are you <span className="fwq-red">building?</span>
    </h1>
    <p className="fwq-sub">
      Pick the closest fit — we'll ask the right follow-ups next.
    </p>
    <div className="fwq-grid-3">
      {PROJECT_TYPES.map((p) => (
        <ChoiceCard
          key={p.id}
          selected={data.projectType === p.id}
          onClick={() => set({ projectType: p.id })}
        >
          <div className="fwq-card__title">{p.label}</div>
          <div className="fwq-card__hint">{p.hint}</div>
        </ChoiceCard>
      ))}
    </div>
  </div>
);

const StepPlatforms = ({ data, set }) => {
  const sel = data.platforms || [];
  const toggle = (p) =>
    set({
      platforms: sel.includes(p) ? sel.filter((x) => x !== p) : [...sel, p],
    });
  return (
    <div>
      <QMeta>QUESTION 02</QMeta>
      <h1 className="fwq-h1">
        Which <span className="fwq-italic">platforms?</span>
      </h1>
      <p className="fwq-sub">
        Select all that apply. Not sure? Pick what your users will use most.
      </p>
      <div className="fwq-chips" role="group" aria-label="Platforms">
        {PLATFORMS.map((p) => (
          <Chip key={p} selected={sel.includes(p)} onClick={() => toggle(p)}>
            {p}
          </Chip>
        ))}
      </div>
    </div>
  );
};

const StepFeatures = ({ data, set }) => {
  const sel = data.features || [];
  const toggle = (f) =>
    set({
      features: sel.includes(f) ? sel.filter((x) => x !== f) : [...sel, f],
    });
  return (
    <div>
      <QMeta>QUESTION 03</QMeta>
      <h1 className="fwq-h1">
        What does it <span className="fwq-red">do?</span>
      </h1>
      <p className="fwq-sub">
        Pick any features you know you need. We'll surface the rest in a
        discovery call.
      </p>
      <div
        className="fwq-chips fwq-chips--gap"
        role="group"
        aria-label="Features"
      >
        {FEATURES.map((f) => (
          <Chip key={f} selected={sel.includes(f)} onClick={() => toggle(f)}>
            {f}
          </Chip>
        ))}
      </div>
      <label htmlFor="featuresNote">
        <QMeta>SOMETHING SPECIFIC WE MISSED?</QMeta>
      </label>
      <TextInput
        id="featuresNote"
        value={data.featuresNote}
        onChange={(v) => set({ featuresNote: v })}
        placeholder="e.g. we need offline sync with conflict resolution"
      />
    </div>
  );
};

const StepTimeline = ({ data, set }) => (
  <div>
    <QMeta>QUESTION 04</QMeta>
    <h1 className="fwq-h1">
      When do you need it <span className="fwq-italic">live?</span>
    </h1>
    <div className="fwq-grid-2">
      {TIMELINES.map((t) => (
        <ChoiceCard
          key={t}
          selected={data.timeline === t}
          onClick={() => set({ timeline: t })}
        >
          <div className="fwq-card__title fwq-card__title--sm">{t}</div>
        </ChoiceCard>
      ))}
    </div>
  </div>
);

const StepBudget = ({ data, set }) => (
  <div>
    <QMeta>QUESTION 05</QMeta>
    <h1 className="fwq-h1">
      What's your <span className="fwq-red">budget?</span>
    </h1>
    <p className="fwq-sub">
      Every project starts at <strong>{START_PRICE}</strong>. We'll come back
      with a firm quote after a discovery call — no surprises. Builds past{" "}
      <strong>80 hours</strong> convert to a fixed flat-rate quote, or hourly
      at <strong>$120/h</strong> if you'd rather stay flexible — locked in
      writing before anything bills.
    </p>
    <div className="fwq-grid-2">
      {BUDGET_TIERS.map((b) => (
        <ChoiceCard
          key={b.id}
          selected={data.budget === b.id}
          onClick={() => set({ budget: b.id })}
        >
          <div className="fwq-card__budget">{b.label}</div>
          <div className="fwq-card__hint">{b.hint}</div>
        </ChoiceCard>
      ))}
    </div>
  </div>
);

const StepContact = ({ data, set, showValidation }) => {
  const nameMissing = showValidation && !(data.name || "").trim();
  const emailInvalid = showValidation && !emailRe.test(data.email || "");
  return (
    <div>
      <QMeta>QUESTION 06</QMeta>
      <h1 className="fwq-h1">
        About <span className="fwq-italic">you.</span>
      </h1>
      <div className="fwq-form-grid">
        <div>
          <label htmlFor="name">
            <QMeta>NAME</QMeta>
          </label>
          <TextInput
            id="name"
            value={data.name}
            onChange={(v) => set({ name: v })}
            placeholder="First, last"
          />
          {nameMissing && (
            <div className="fwq-field-err">Name is required.</div>
          )}
        </div>
        <div>
          <label htmlFor="email">
            <QMeta>EMAIL</QMeta>
          </label>
          <TextInput
            id="email"
            type="email"
            value={data.email}
            onChange={(v) => set({ email: v })}
            placeholder="you@company.com"
          />
          {emailInvalid && (
            <div className="fwq-field-err">Enter a valid email.</div>
          )}
        </div>
        <div>
          <label htmlFor="company">
            <QMeta>COMPANY / PROJECT NAME</QMeta>
          </label>
          <TextInput
            id="company"
            value={data.company}
            onChange={(v) => set({ company: v })}
            placeholder="Optional"
          />
        </div>
        <div>
          <label htmlFor="referral">
            <QMeta>HOW'D YOU HEAR ABOUT US?</QMeta>
          </label>
          <TextInput
            id="referral"
            value={data.referral}
            onChange={(v) => set({ referral: v })}
            placeholder="Referral, socials, search…"
          />
        </div>
        <div className="fwq-form-grid__full">
          <label htmlFor="notes">
            <QMeta>ANYTHING ELSE WE SHOULD KNOW?</QMeta>
          </label>
          <TextInput
            id="notes"
            multi
            value={data.notes}
            onChange={(v) => set({ notes: v })}
            placeholder="The pitch, the audience, the why…"
          />
        </div>
      </div>
    </div>
  );
};

const StepReview = ({ data }) => {
  const featureList = data.features || [];
  const featureText =
    featureList.length === 0
      ? "—"
      : featureList.slice(0, 4).join(", ") +
        (featureList.length > 4 ? ` +${featureList.length - 4} more` : "");
  const rows = [
    [
      "Project type",
      PROJECT_TYPES.find((p) => p.id === data.projectType)?.label || "—",
    ],
    ["Platforms", (data.platforms || []).join(", ") || "—"],
    ["Features", featureText],
    ["Timeline", data.timeline || "—"],
    ["Budget", BUDGET_TIERS.find((b) => b.id === data.budget)?.label || "—"],
    ["Name", data.name || "—"],
    ["Email", data.email || "—"],
  ];
  return (
    <div>
      <QMeta>LAST STEP · REVIEW</QMeta>
      <h1 className="fwq-h1 fwq-h1--tight">
        Does this look <span className="fwq-red">right?</span>
      </h1>
      <div className="fwq-review">
        {rows.map((r, i) => (
          <div key={i} className="fwq-review__row">
            <QMeta>{r[0]}</QMeta>
            <div className="fwq-review__val">{r[1]}</div>
          </div>
        ))}
      </div>
      <p className="fwq-foot-note">
        We read every submission. You'll hear back within 24 hours with next
        steps — usually a proposed discovery call time.
      </p>
    </div>
  );
};

const BrowsingOutcome = () => (
  <div className="fwq-browsing">
    <QMeta>NO PROJECT YET · THAT'S FINE</QMeta>
    <h1 className="fwq-h1">
      Here's the <span className="fwq-red">starting point.</span>
    </h1>
    <div className="fwq-browsing__card">
      <QMeta>PROJECTS START AT</QMeta>
      <div className="fwq-browsing__price">$8,850</div>
      <div className="fwq-browsing__body">
        That covers a focused single-platform build — brand, design, and
        shipping a working first version. Bigger scope goes up from there.
      </div>
    </div>
  </div>
);

export default Questionnaire;
